import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Cell,
  DataBlock,
  DataTable,
  DataTableRow,
  Divider,
  Expander,
  ExpanderContent,
  ExpanderHeader,
  Grid,
  Row,
} from '@fs/zion-ui'
import { Thinganator } from '@fs/zion-ui/quarks-authorized-use-only'
import { v4 } from 'uuid'
import { Link } from '@fs/zion-router'
import useNotifications from '../../../../hooks/useNotifications'
import { getOdmiRecords, getOdmiRecordsCount } from '../../../../services/OdvService'
import { getFrontendBaseUrl } from '../../../../util/UrlUtil'

export default function OdmiRecordsView({ odmiQuery, country }) {
  const { t } = useTranslation()
  const { error, ok } = useNotifications()
  const [recordCount, setRecordCount] = useState(0)
  const [page, setPage] = useState(0)
  const [records, setRecords] = useState([])
  const [noResults, setNoResults] = useState(false)

  useEffect(() => {
    setNoResults(false)
    setRecords([])
    getOdmiRecordsCount(country, odmiQuery)
      .then((response) => {
        if (response?.data?.recordCount) {
          setPage(0)
          setRecordCount(response.data.recordCount)
          ok(t('odv.found.total', 'Found { count } total records', { count: response.data.recordCount }))
        } else {
          setNoResults(true)
        }
      })
      .catch((err) => {
        setNoResults(true)
        error(
          t('odv.search.errors.gettingOdmiRecordCountResults', {
            defaultValue: `Error getting ODMI record count results: ${err.message || ''}`,
            message: err.message,
          }),
          err
        )
      })

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [odmiQuery])

  useEffect(() => {
    getOdmiRecords(country, odmiQuery, page)
      .then((response) => {
        if (response.data) {
          setRecords(response.data)
        }
      })
      .catch((err) => {
        error(
          t('odv.search.errors.gettingOdmiRecordSearchResults', {
            defaultValue: `Error getting ODMI record search results: ${err.message || ''}`,
            message: err.message,
          }),
          err
        )
      })

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recordCount, page])

  const getType = () => {
    let type = null
    switch (country) {
      case 'china':
        type = 'ODMIC'
        break
      case 'korea':
        type = 'ODMIK'
        break
      case 'japan':
        type = 'ODMIJ'
        break
      case 'cyrillic':
        type = 'ODMIF'
        break
      default:
        break
    }
    return type
  }

  const odmiIndividual = (record) => {
    return (
      <Grid>
        <Cell columns={1}>
          <DataBlock
            heading={t('id', 'ID')}
            data={
              <Link
                to={`${getFrontendBaseUrl()}/search/odm?id=${
                  record?.individual?.permanentRecFileNumber?.value
                }&odmType=${getType()}`}
                target="_blank"
                noScrollTop
              >
                {record?.individual?.permanentRecFileNumber?.value}
              </Link>
            }
          />
        </Cell>
        <Cell columns={1}>
          <DataBlock
            heading={t('sex', 'Sex')}
            data={t(`gender.${record?.individual?.sex?.value?.toLowerCase()}`, record?.individual?.sex?.value)}
          />
        </Cell>
        {record?.individual?.events?.[0] && (
          <Cell columns={1}>
            <DataBlock
              heading={t(`odmi.event.${record?.individual?.events?.[0]?.type?.toLowerCase()}`, t('event', 'Event'))}
              data={record.individual?.events?.[0]?.date?.value}
            />
          </Cell>
        )}
        {!record?.individual?.events?.[0] && (
          <Cell columns={1}>
            <p />
          </Cell>
        )}
        <Cell columns={1}>
          <DataBlock
            heading={t('membership.livingStatus', 'Living Status')}
            data={t(record?.extras?.livingState?.toLowerCase(), record?.extras?.livingState)}
          />
        </Cell>
        {record?.individual?.names?.map((name) => {
          return (
            <Cell columns={3} key={v4()}>
              <DataBlock
                heading={t(name?.prefix?.value?.toLowerCase(), name?.prefix?.value)}
                data={`/${name?.surname?.value}/  ${name?.givenName?.value}`}
              />
            </Cell>
          )
        })}
      </Grid>
    )
  }

  function getEvents(record) {
    const events = record.individual?.events
    return (
      <>
        {events?.length > 0 && (
          <DataTable
            columns={[
              { label: t('odm.event.type', 'Type'), size: 'xs' },
              { label: t('date', 'Date'), size: 'md' },
              { label: t('place', 'Place'), size: 'md' },
            ]}
          >
            {events.map((event) => {
              return (
                <DataTableRow
                  key={v4()}
                  column1={t(`odmi.event.${event.type?.toLowerCase()}`, event.type)}
                  column2={event?.date?.value}
                  column3={event?.place?.placeName}
                />
              )
            })}
          </DataTable>
        )}
        {!events || (events.length <= 0 && <p>{t('odm.noEvents', 'No Events')}</p>)}
      </>
    )
  }

  function getOrdinances(record) {
    const ordinances = record.individual?.ldsIndividualOrdinances
    return (
      <>
        {ordinances?.length > 0 && (
          <DataTable
            columns={[
              { label: t('type', 'Type'), size: 'sm' },
              { label: t('date', 'Date'), size: 'sm' },
              { label: t('temple.label', 'Temple'), size: 'sm' },
              { label: t('status.label', 'Status'), size: 'sm' },
              { label: t('source', 'Source'), size: 'sm' },
              { label: t('tpin', 'TPIN'), size: 'sm' },
            ]}
          >
            {ordinances.map((ordinance) => {
              return (
                <DataTableRow
                  key={v4()}
                  column1={t(`ordinances.types.${ordinance.type.toLowerCase()}`, ordinance.type)}
                  column2={ordinance.date?.value}
                  column3={ordinance.temple?.value}
                  column4={ordinance.status?.value}
                  column5={ordinance.customTags?.[0]?.value}
                  column6={
                    ordinance.customTags?.[1]?.value && (
                      <Link
                        to={`${getFrontendBaseUrl()}/temple/ows/${ordinance.customTags[1].value}/summary`}
                        target="_blank"
                        noScrollTop
                      >
                        {ordinance.customTags[1].value}
                      </Link>
                    )
                  }
                />
              )
            })}
          </DataTable>
        )}
        {!ordinances || (ordinances.length <= 0 && <p>{t('odm.noOrdinances', 'No Ordinances')}</p>)}
      </>
    )
  }

  function getSubmitter(record) {
    const submitters = record.individual?.submitters
    return (
      <>
        {submitters && submitters.length > 0 && (
          <Grid>
            <Cell columns={4}>
              <DataBlock heading={t('name', 'Name')} data={submitters[0].name?.value} />
            </Cell>
            <Cell columns={4}>
              <DataBlock heading={t('phone', 'Phone')} data={submitters[0].phoneNumbers?.[0]?.value} />
            </Cell>
            <Cell columns={3}>
              <DataBlock
                heading={t('address', 'Address')}
                data={
                  <>
                    {submitters[0].address?.lines?.[0]}
                    <br />
                    {submitters[0].address?.lines?.[1]}
                    <br />
                    {submitters[0].address?.lines?.[2]}
                  </>
                }
              />
            </Cell>
          </Grid>
        )}
        {!submitters || (submitters.length <= 0 && <p>{t('odm.noSubmitter', 'No Submitter Information')}</p>)}
      </>
    )
  }

  function getParentOrSpouse(person) {
    return (
      <>
        {person && (
          <Grid>
            <Cell columns={1}>
              <DataBlock heading={t('id', 'ID')} data={person.permanentRecFileNumber?.value} />
            </Cell>
            <Cell columns={1}>
              <DataBlock
                heading={t('membership.livingStatus', 'Living Status')}
                data={t(person.customTags?.[0]?.value?.toLowerCase(), person.customTags?.[0]?.value)}
              />
            </Cell>
            {person.names?.map((name) => {
              return (
                <Cell columns={3} key={v4()}>
                  <DataBlock
                    heading={t(name?.prefix?.value?.toLowerCase(), name?.prefix?.value)}
                    data={`/${name?.surname?.value || ''}/  ${name?.givenName?.value}`}
                  />
                </Cell>
              )
            })}
          </Grid>
        )}
        {!person && <p>{t('personNotFound', 'Person Not Found')}</p>}
      </>
    )
  }

  function getChild(child) {
    return (
      <Grid key={v4()}>
        <Cell columns={1}>
          <DataBlock heading={t('id', 'ID')} data={child.children?.[0]?.value} />
        </Cell>
        <Cell columns={1}>
          <DataBlock
            heading={t('sex', 'Sex')}
            data={t(`gender.${child?.children?.[1]?.value?.toLowerCase()}`, child?.children?.[1]?.value)}
          />
        </Cell>
        <Cell columns={2}>
          <DataBlock
            heading={t('membership.livingStatus', 'Living Status')}
            data={t(child?.children?.[2]?.value?.toLowerCase(), child.customTags?.[2]?.value)}
          />
        </Cell>
        {child.children?.[3]?.children &&
          child.children[3].children.map((name) => {
            return (
              <Cell columns={2} key={v4()}>
                <DataBlock heading={t(name.tag.toLowerCase(), name.tag)} data={name.value} />
              </Cell>
            )
          })}
      </Grid>
    )
  }

  function getNotes(notes) {
    return (
      <>
        {notes?.[0]?.lines?.length > 0 && (
          <Grid>
            <Cell columns={10}>
              <p>{notes[0].lines?.[0]}</p>
            </Cell>
          </Grid>
        )}
        {(!notes || notes?.[0]?.lines?.length === 0) && <p>{t('noInformation', 'No Information')}</p>}
      </>
    )
  }

  return (
    <>
      {!noResults && (
        <>
          <Divider marginY="nano" />
          <Row alignX="end">
            <Thinganator
              label={t('odmi.page', 'Page')}
              index={page}
              setIndex={setPage}
              length={Math.ceil(recordCount / 100)}
            />
          </Row>
          <Divider marginY="nano" />
          {!noResults &&
            records.map((record) => {
              return (
                <React.Fragment key={record?.individual?.permanentRecFileNumber?.value || v4()}>
                  <Expander>
                    <ExpanderHeader>{odmiIndividual(record)}</ExpanderHeader>
                    <ExpanderContent>
                      <Grid>
                        <Cell columns={1}>
                          <Divider />
                        </Cell>
                        <Cell columns={11}>
                          <Divider label={t('odm.events', 'Events')} />
                          {getEvents(record)}
                          <Divider label={t('father', 'Father')} marginY="xxs" />
                          {getParentOrSpouse(record.individual.familiesWhereChild[0]?.family?.husband)}
                          <Divider label={t('mother', 'Mother')} marginY="xxs" />
                          {getParentOrSpouse(record.individual.familiesWhereChild[0]?.family?.wife)}
                          <Divider label={t('spouse', 'Spouse')} marginY="xxs" />
                          {getParentOrSpouse(record.individual.spouses[0])}
                          <Divider label={t('children', 'Children')} marginY="xxs" />
                          {record.individual.customTags?.[1]?.children &&
                            record.individual.customTags[1].children.length > 0 &&
                            record.individual.customTags[1].children.map((child) => {
                              return getChild(child)
                            })}
                          {!record.individual.customTags?.[1]?.children ||
                            (record.individual.customTags[1]?.children?.length === 0 && <p>{t('none', 'None')}</p>)}
                          <Divider label={t('ordinances', 'Ordinances')} marginY="sm" />
                          {getOrdinances(record)}
                          <Divider label={t('submitter', 'Submitter')} marginY="xs" />
                          {getSubmitter(record)}
                          <Divider label={t('source', 'Source')} marginY="xs" />
                          <Grid>
                            <Cell columns={10}>
                              <DataBlock heading={t('system', 'System')} data={record.extras.sourceSubmissionTypeId} />
                            </Cell>
                          </Grid>
                          <Divider label={t('notes', 'Notes')} marginY="xs" />
                          {getNotes(record.individual.notes)}
                        </Cell>
                      </Grid>
                    </ExpanderContent>
                  </Expander>
                  <Divider marginY="nano" />
                </React.Fragment>
              )
            })}
        </>
      )}
      {noResults && <p id="odmiRecordNoResults">{t('noResults', 'No Results Found')}</p>}
    </>
  )
}
